import {
  BookOpenIcon,
  ComputerDesktopIcon,
  PencilIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import {
  ClassLessonItem,
  ClassLessonSubjectItem,
  ContentType,
  SubjectCodeType,
} from '../../../constants/types/lms/take-class/type';
import TextWinds from '../../common/TextWinds';
import { Button } from 'antd';
import { formatToKstDate } from '../../../api/utils/util-func';

interface SubjectResourceRowProps {
  bookCode: string;
  lesson: ClassLessonItem;
  subject: ClassLessonSubjectItem;
  onPptClick?: (
    lesson: ClassLessonItem,
    subjectType: ClassLessonSubjectItem,
  ) => void;
  onVideoClick?: (
    lesson: ClassLessonItem,
    subjectType: ClassLessonSubjectItem,
  ) => void;
  onSelfClick?: (
    lesson: ClassLessonItem,
    subjectType: ClassLessonSubjectItem,
  ) => void;
  onReadingOnClick?: (
    lesson: ClassLessonItem,
    subjectType: ClassLessonSubjectItem,
  ) => void;
}

const SUBJECT_COLOR_CLASS_NAME_MAP: Record<SubjectCodeType, string> = {
  ESB: 'bg-red-200 text-black',
  EF: 'bg-blue-200 text-black',
  EC: 'bg-yellow-300 text-black',
  EFP: 'bg-[#D8EFF9] text-black',
  MATERIAL: 'bg-[#F1F3F5] text-black',
};

const CONTENT_TYPE_LABEL_MAP: Record<ContentType, string> = {
  PPT: 'PPT',
  VIDEO: '동영상',
  SELF: '홈이',
  READING_ON: '리딩온',
};

/**
 * 개편 LMS (교사) - 수업하기 페이지 > 북별 레슨 판넬 > 레슨 목록 > 레슨 카드 > 레슨별 과목 행
 */
const SubjectResourceRow = ({
  bookCode,
  lesson,
  subject,
  onPptClick,
  onVideoClick,
  onSelfClick,
  onReadingOnClick,
}: SubjectResourceRowProps) => {
  const WIDE_BOOK_CODES = ['Tiki-Taka', 'Speech Plus'];

  const shouldUseWideSubjectColumn = WIDE_BOOK_CODES.includes(bookCode);

  return (
    <div
      className={[
        'grid border-b border-gray2 last:border-b-0',
        shouldUseWideSubjectColumn
          ? 'xl:grid-cols-[230px_1fr_260px]'
          : 'xl:grid-cols-[130px_1fr_260px]',
      ].join(' ')}
    >
      <div className="flex items-center px-4 py-2">
        <span
          className={[
            'mr-2 h-2.5 w-2.5 shrink-0 rounded-full',
            SUBJECT_COLOR_CLASS_NAME_MAP[subject.subjectCode],
          ].join(' ')}
        />

        <TextWinds type="content_body3" color="gray9" className="font-bold">
          {subject.subjectName}
        </TextWinds>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 py-2">
        <ResourceButton
          label="PPT"
          variant="ppt"
          disabled={!subject.hasPpt}
          onClick={() => onPptClick?.(lesson, subject)}
        />

        <ResourceButton
          label="동영상"
          variant="video"
          disabled={!subject.hasVideo}
          onClick={() => onVideoClick?.(lesson, subject)}
        />

        <ResourceButton
          label="홈이"
          variant="self"
          disabled={!subject.hasSelf}
          onClick={() => onSelfClick?.(lesson, subject)}
        />

        <ResourceButton
          label="리딩온"
          variant="reading"
          disabled={!subject.hasReadingOn}
          onClick={() => onReadingOnClick?.(lesson, subject)}
        />
      </div>

      <div className="flex h-full flex-col justify-center px-4 py-2">
        <TextWinds type="content_body3" color="gray9">
          최근 열람 자료 :{' '}
          {subject.lastViewedContentType
            ? (CONTENT_TYPE_LABEL_MAP[subject.lastViewedContentType] ??
              subject.lastViewedContentType)
            : '-'}
        </TextWinds>

        <TextWinds type="content_body3" color="gray9">
          최근 열람일 :{' '}
          {subject.lastViewedAt
            ? formatToKstDate(subject.lastViewedAt, 'YYYY.MM.DD HH:mm')
            : '-'}
        </TextWinds>
      </div>
    </div>
  );
};

export default SubjectResourceRow;

interface ResourceButtonProps {
  label: string;
  variant: 'ppt' | 'video' | 'self' | 'reading';
  disabled?: boolean;
  onClick?: () => void;
}

const RESOURCE_BUTTON_STYLE_MAP: Record<
  ResourceButtonProps['variant'],
  {
    icon: React.ReactNode;
    colorClassName: string;
  }
> = {
  ppt: {
    icon: <ComputerDesktopIcon className="h-4 w-4" />,
    colorClassName: 'text-[#FF6B4A]',
  },
  video: {
    icon: <PlayIcon className="h-4 w-4" />,
    colorClassName: 'text-[#1E8DD8]',
  },
  self: {
    icon: <PencilIcon className="h-4 w-4" />,
    colorClassName: 'text-[#00A985]',
  },
  reading: {
    icon: <BookOpenIcon className="h-4 w-4" />,
    colorClassName: 'text-[#6B5ADD]',
  },
};

const ResourceButton = ({
  label,
  variant,
  disabled = false,
  onClick,
}: ResourceButtonProps) => {
  if (disabled) return null;

  const style = RESOURCE_BUTTON_STYLE_MAP[variant];

  return (
    <Button
      type="default"
      className="flex h-7 min-w-[76px] items-center justify-center gap-1 rounded-lg !border-gray4 !bg-white px-2.5 hover:!border-gray5 hover:!bg-gray0"
      onClick={onClick}
    >
      <span className={style.colorClassName}>{style.icon}</span>

      <TextWinds type="caption1" color="gray9">
        {label}
      </TextWinds>
    </Button>
  );
};
